<template>
  <!-- <tbody> -->
  <tr class="text-sm hover:bg-gray-50 cursor-pointer" @click="expanded = !expanded">
    <td class="py-2 px-3 border border-l-0" />
    <td class="py-2 px-3 border w-px align-top">
      <svg viewBox="0 0 24 24" class="w-5 h-5 inline-block text-gray-500" fill="currentColor">
        <path fill="none" d="M0 0h24v24H0z" />
        <path
          v-if="expanded"
          d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10
            10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0
            16zm-5-9h10v2H7v-2z"
        />
        <path
          v-else
          d="M11 11V7h2v4h4v2h-4v4h-2v-4H7v-2h4zm1 11C6.477 22 2 17.523
            2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0
            0-16 8 8 0 0 0 0 16z"
        />
      </svg>
    </td>
    <td class="py-2 px-3 align-top border">
      {{ gaCampaign.name }}
      <template v-if="expanded">
        <Portal :to="`portal-ga-campaign-${gaCampaign.id}`">
          <tr class="text-sm text-gray-600 bg-gray-100">
            <td class="py-2 px-3 border border-l-0" />
            <td class="py-2 px-3 border w-px" />
            <td class="py-2 px-3 border text-left">
              <span class="inline-block"> Ad name </span>
            </td>
            <td class="py-2 px-3 border text-right">Impressions</td>
            <td class="py-2 px-3 border text-right">Reach</td>
            <td class="py-2 px-3 border text-right">Frequency</td>
            <td class="py-2 px-3 border text-right">Pageviews</td>
            <td class="py-2 px-3 border text-right">Contact Requests</td>
            <td class="py-2 px-3 border border-r-0 text-right">Spend</td>
          </tr>
        </Portal>
        <template v-for="gaAd in gaAds">
          <Portal
            :to="`portal-ga-campaign-${gaCampaign.id}-ga-ad-${gaAd.id}`"
            :key="`${gaCampaign.id}:${gaAd.id}`"
          >
            <GoogleAdsAdTableRow
              :ga-ad="gaAd"
              :ga-ad-report="gaCampaignReports.find((x) => x.ga_ad_id === gaAd.id)"
            />
          </Portal>
        </template>
      </template>
    </td>
    <td class="py-2 align-top px-3 border text-right">
      {{ gaCampaignTotalsFormatted.impressions }}
      <br />
      {{ gaCampaignTotalsFormatted.cpi }}
    </td>
    <td class="py-2 align-top px-3 border text-right">
      {{ gaCampaignTotalsFormatted.interactions }}
      <br />
      {{ gaCampaignTotalsFormatted.cpr }}
    </td>
    <td class="py-2 align-top px-3 border text-right">-</td>
    <td class="py-2 align-top px-3 border text-right">
      {{ gaCampaignTotalsFormatted.clicks }}
      <br />
      {{ pageviewsPerImpression }}% / {{ costPerPageView }}
    </td>
    <td class="py-2 align-top px-3 border text-right">-</td>
    <td class="py-2 align-top px-3 border border-r-0 text-right">
      {{ gaCampaignTotalsFormatted.cost }}
    </td>
  </tr>
</template>

<script>
import Numbro from '@/utils/numbro';
import GoogleAdsAdTableRow from './GoogleAdsAdTableRow.vue';

export default {
  components: {
    GoogleAdsAdTableRow,
  },
  props: {
    gaCampaign: { type: Object, required: true },
    gaCampaignReports: { type: Array, default: () => [] },
  },
  data() {
    return {
      expanded: false,
    };
  },
  computed: {
    gaAds() {
      return this.gaCampaign.ga_ads || [];
    },
    gaCampaignTotals() {
      return {
        impressions: 0,
        cost_micros: 0,
        clicks: 0,
        engagements: 0,
        interactions: 0,
        ...this.gaCampaignReports.find(
          (report) =>
            report.ga_campaign_id === this.gaCampaign.id &&
            report.ga_ad_group_id === 0 &&
            report.ga_ad_id === 0
        ),
      };
    },
    gaCampaignTotalsFormatted() {
      const impressions = this.gaCampaignTotals?.impressions || 0;
      const clicks = this.gaCampaignTotals?.clicks || 0;
      const engagements = this.gaCampaignTotals?.engagements || 0;
      const interactions = this.gaCampaignTotals?.interactions || 0;
      const cost = (this.gaCampaignTotals?.cost_micros || 0) / 1_000_000;
      const cpi = impressions ? 1000 * (cost / impressions) : 0;
      const cpr = interactions ? 1000 * (cost / interactions) : 0;

      return {
        impressions: Numbro(impressions).format({ thousandSeparated: true }),
        clicks: Numbro(clicks).format({ thousandSeparated: true }),
        engagements: Numbro(engagements).format({ thousandSeparated: true }),
        interactions: Numbro(interactions).format({ thousandSeparated: true }),
        cost: Numbro(cost).formatCurrency({ thousandSeparated: true, mantissa: 2 }),
        cpi: Numbro(cpi).formatCurrency({ thousandSeparated: true, mantissa: 2 }),
        cpr: Numbro(cpr).formatCurrency({ thousandSeparated: true, mantissa: 2 }),
      };
    },
    pageviewsPerImpression() {
      const { impressions, clicks } = this.gaCampaignTotals;
      if (!impressions) return 0;
      return Numbro((clicks / impressions) * 100).format({ thousandSeparated: true, mantissa: 2 });
    },
    costPerPageView() {
      const { clicks } = this.gaCampaignTotals;
      const cost = (this.gaCampaignTotals?.cost_micros || 0) / 1_000_000;
      if (!clicks) return 0;
      return Numbro(cost / clicks).formatCurrency({ thousandSeparated: true, mantissa: 2 });
    },
  },
};
</script>
